import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GRANULARITIES } from '../../utils/cubeQuery';
import PeriodSelect from '../common/PeriodSelect';
import { FieldIcon, IconClose, IconGrip, IconPlus, IconFilter, IconSort, IconCalendar } from './icons';
import FieldPicker from './FieldPicker';
import FilterEditor, { describeFilter } from './FilterEditor';
import CalculationEditor from './CalculationEditor';
import { CALCULATIONS, calculationInfo, describeCalculation } from './calculations';

const SHELF_ACCEPTS = {
  values: (f) => f.kind === 'measure',
  groups: (f) => f.kind === 'dimension' || f.kind === 'time',
  time: (f) => f.kind === 'time',
  filters: (f) => f.kind === 'measure' || f.kind === 'dimension',
};

/** Drop target holding an ordered list of field chips. */
const Shelf = ({ id, title, icon, hint, count, dragRef, statusOf, onDropField, children, onAdd, addLabel, picker }) => {
  const [over, setOver] = useState(false);
  const accepts = (payload) => !!payload && SHELF_ACCEPTS[id](payload.field) && (payload.from !== 'browser' || statusOf(payload.field).ok);
  return (
    <section
      className={`rf-shelf ${over ? 'rf-shelf--over' : ''}`}
      aria-label={title}
      data-shelf={id}
      onDragOver={(e) => {
        if (!accepts(dragRef.current)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = dragRef.current.from === 'browser' ? 'copy' : 'move';
        if (!over) setOver(true);
      }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
      onDrop={(e) => {
        const payload = dragRef.current;
        setOver(false);
        if (!accepts(payload)) return;
        e.preventDefault();
        // The chip under the pointer decides the position; otherwise append.
        const chip = e.target.closest?.('[data-chip-index]');
        let index = count;
        if (chip) {
          const rect = chip.getBoundingClientRect();
          index = Number(chip.dataset.chipIndex) + (e.clientY > rect.top + rect.height / 2 ? 1 : 0);
        }
        dragRef.current = null;
        onDropField(payload, index);
      }}
    >
      <div className="rf-shelf-head">
        <span className="rf-shelf-icon">{icon}</span>
        <h4>{title}</h4>
        {count > 0 && <span className="rf-count">{count}</span>}
        {onAdd && (
          <button type="button" className="rf-icon-btn rf-shelf-add" onClick={onAdd} aria-label={addLabel} title={addLabel}>
            <IconPlus size={15} />
          </button>
        )}
      </div>
      {picker}
      <div className="rf-shelf-body">
        {children}
        {count === 0 && <div className="rf-shelf-hint">{hint}</div>}
      </div>
    </section>
  );
};

/** A field chip: drag to reorder or move between shelves, sort, remove. */
const Chip = ({ field, name, shelf, index, dragRef, sortState, onSort, onRemove, children }) => {
  const label = field?.label || name.split('.').pop();
  return (
    <div
      className={`rf-chip rf-chip--${field?.kind || 'dimension'}`}
      data-chip-index={index}
      draggable
      onDragStart={(e) => {
        dragRef.current = { field, from: shelf, index };
        e.dataTransfer.setData('text/plain', name);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => { dragRef.current = null; }}
    >
      <span className="rf-chip-grip" aria-hidden="true"><IconGrip size={13} /></span>
      <span className="rf-chip-icon">{field && <FieldIcon field={field} size={13} />}</span>
      <span className="rf-chip-text">
        <span className="rf-chip-label">{label}</span>
        {field?.cubeTitle && <span className="rf-chip-cube">{field.cubeTitle}</span>}
      </span>
      {children}
      {onSort && (
        <button
          type="button"
          className={`rf-chip-btn rf-chip-sort ${sortState ? 'rf-chip-sort--on' : ''}`}
          onClick={onSort}
          aria-label={`Sort by ${label}${sortState ? ` (${sortState === 'asc' ? 'ascending' : 'descending'})` : ''}`}
          title={sortState ? `Sorted ${sortState === 'asc' ? 'ascending' : 'descending'}. Click to change.` : 'Sort by this field'}
        >
          {sortState === 'asc' ? '↑' : sortState === 'desc' ? '↓' : <IconSort size={13} />}
        </button>
      )}
      <button type="button" className="rf-chip-btn" onClick={onRemove} aria-label={`Remove ${label}`} title="Remove">
        <IconClose size={13} />
      </button>
    </div>
  );
};

const COMPARE_OPTIONS = [
  { value: '', label: 'Nothing' },
  { value: 'previous', label: 'The previous period' },
  { value: 'lastYear', label: 'The same period last year' },
];

const newId = () => Math.random().toString(36).slice(2, 8);

/** The list of calculation types, opened from the Calculations shelf. */
const CalculationMenu = ({ onPick, onClose }) => {
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (e.type === 'keydown' ? e.key === 'Escape' : !ref.current?.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    ref.current?.querySelector('button')?.focus();
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', close); };
  }, [onClose]);
  return (
    <div className="rf-calc-menu" role="menu" aria-label="Add a calculation" ref={ref}>
      {CALCULATIONS.map((c) => (
        <button key={c.type} type="button" role="menuitem" onClick={() => onPick(c.type)}>
          <strong>{c.name}</strong>
          <span>{c.hint}</span>
        </button>
      ))}
    </div>
  );
};

/** Show only the top N values of a group, with the rest added up as "Other" or left out. */
const TopValues = ({ form, labelOf, update }) => {
  const topN = form.topN;
  const on = Boolean(topN);
  const set = (patch) => update({ topN: { ...topN, ...patch } });
  const start = () => update({ topN: { dimension: form.dimensions[0], count: 10, by: form.orderMember && form.measures.includes(form.orderMember) ? form.orderMember : form.measures[0], other: true } });
  return (
    <div className="rf-topn">
      <label className="rf-switch">
        <input type="checkbox" checked={on} onChange={(e) => (e.target.checked ? start() : update({ topN: null }))} />
        <span>Show only the top values</span>
      </label>
      {on && (
        <div className="rf-topn-fields" role="group" aria-label="Top values">
          <span className="rf-topn-line">
            <span>Top</span>
            <input type="number" min={1} max={1000} aria-label="How many top values" value={topN.count}
              onChange={(e) => set({ count: e.target.value })} />
            {form.dimensions.length > 1 ? (
              <select aria-label="Top values of" value={topN.dimension} onChange={(e) => set({ dimension: e.target.value })}>
                {form.dimensions.map((d) => <option key={d} value={d}>{labelOf(d)}</option>)}
              </select>
            ) : <strong>{labelOf(topN.dimension)}</strong>}
          </span>
          <span className="rf-topn-line">
            <span>ranked by</span>
            <select aria-label="Ranked by" value={topN.by || form.measures[0]} onChange={(e) => set({ by: e.target.value })}>
              {form.measures.map((m) => <option key={m} value={m}>{labelOf(m)}</option>)}
            </select>
          </span>
          <label className="rf-switch">
            <input type="checkbox" checked={topN.other !== false} onChange={(e) => set({ other: e.target.checked })} />
            <span>Add up the rest as “Other”</span>
          </label>
          <p className="rf-field-note">
            {topN.other !== false
              ? 'The totals and percentages still include every value.'
              : 'The rest are left out, and the totals cover only the top values.'}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Right pane, Build tab: the query as shelves — values, group by, time,
 * filters, sort and limit.
 */
const BuildPanel = ({ form, catalogue, statusOf, dragRef, actions, openFilter, setOpenFilter, loadValues }) => {
  const [picker, setPicker] = useState(null); // shelf id
  const [calcMenu, setCalcMenu] = useState(false);
  const [openCalc, setOpenCalc] = useState(null);
  const labelOf = useCallback((name) => catalogue.fields.get(name)?.label || String(name).split('.').pop(), [catalogue]);
  const calculations = form.calculations || [];
  const setCalculations = (list) => actions.update({ calculations: list });
  const addCalculation = (type) => {
    setCalcMenu(false);
    const info = calculationInfo(type);
    const measure = form.measures[0] || null;
    const id = newId();
    const label = type === 'formula'
      ? `Formula ${calculations.filter((c) => c.type === 'formula').length + 1}`
      : `${measure ? labelOf(measure) : 'Value'} ${info.suffix}`;
    setCalculations([...calculations, { id, type, label, measure: type === 'formula' ? null : measure, formula: type === 'formula' ? '' : undefined, format: null }]);
    setOpenCalc(id);
  };
  const closePicker = useCallback(() => setPicker(null), []);
  const fieldOf = (name) => {
    const f = catalogue.fields.get(name);
    return f ? { ...f, cubeTitle: catalogue.byCube.get(f.cube)?.title } : null;
  };
  const orderOf = (member) => (form.orderMember === member ? form.orderDir : null);
  const cycleSort = (member) => {
    if (form.orderMember !== member) actions.update({ orderMember: member, orderDir: 'desc' });
    else if (form.orderDir === 'desc') actions.update({ orderDir: 'asc' });
    else actions.update({ orderMember: '', orderDir: 'desc' });
  };
  const timeBucket = form.timeDimension && form.granularity ? `${form.timeDimension}.${form.granularity}` : null;

  const pickerFor = (shelf, onPick, label) => picker === shelf && (
    <FieldPicker catalogue={catalogue} accept={SHELF_ACCEPTS[shelf]} statusOf={statusOf} label={label} onClose={closePicker}
      onPick={(f) => { closePicker(); onPick(f); }} />
  );

  const orderable = [
    ...form.measures.map((m) => ({ value: m, label: fieldOf(m)?.label || m })),
    ...form.dimensions.map((d) => ({ value: d, label: fieldOf(d)?.label || d })),
    ...(timeBucket ? [{ value: timeBucket, label: `${fieldOf(form.timeDimension)?.label || form.timeDimension} (${form.granularity})` }] : []),
  ];

  return (
    <div className="rf-build">
      <Shelf id="values" title="Values" icon={<FieldIcon field={{ kind: 'measure' }} size={14} />} hint="Add measures to aggregate, such as a sum or count."
        count={form.measures.length} dragRef={dragRef} statusOf={statusOf}
        onDropField={(p, i) => actions.drop(p, 'values', i)}
        onAdd={() => setPicker('values')} addLabel="Add a value"
        picker={pickerFor('values', (f) => actions.add(f, 'values'), 'Add a value')}>
        {form.measures.map((m, i) => (
          <Chip key={m} name={m} field={fieldOf(m)} shelf="values" index={i} dragRef={dragRef}
            sortState={orderOf(m)} onSort={() => cycleSort(m)} onRemove={() => actions.remove(m)} />
        ))}
      </Shelf>

      <section className="rf-shelf" aria-label="Calculations">
        <div className="rf-shelf-head">
          <span className="rf-shelf-icon" aria-hidden="true">ƒ</span>
          <h4>Calculations</h4>
          {calculations.length > 0 && <span className="rf-count">{calculations.length}</span>}
          <button type="button" className="rf-icon-btn rf-shelf-add" onClick={() => setCalcMenu((v) => !v)} aria-label="Add a calculation"
            title={form.measures.length ? 'Add a calculation' : 'Add a value first'} disabled={form.measures.length === 0} aria-expanded={calcMenu}>
            <IconPlus size={15} />
          </button>
        </div>
        {calcMenu && <CalculationMenu onPick={addCalculation} onClose={() => setCalcMenu(false)} />}
        <div className="rf-shelf-body">
          {calculations.map((c) => (openCalc === c.id ? (
            <CalculationEditor key={c.id} calc={c} form={form} labelOf={labelOf}
              onChange={(next) => setCalculations(calculations.map((x) => (x.id === c.id ? next : x)))}
              onRemove={() => { setOpenCalc(null); setCalculations(calculations.filter((x) => x.id !== c.id)); }}
              onDone={() => setOpenCalc(null)} />
          ) : (
            <div key={c.id} className="rf-filter-chip rf-calc-chip">
              <button type="button" className="rf-filter-chip-main" onClick={() => setOpenCalc(c.id)} aria-label={`Edit calculation: ${c.label}`}>
                <span className="rf-calc-chip-icon" aria-hidden="true">ƒ</span>
                <span className="rf-calc-chip-text"><strong>{c.label}</strong><small>{describeCalculation(c, labelOf)}</small></span>
              </button>
              <button type="button" className="rf-chip-btn" onClick={() => setCalculations(calculations.filter((x) => x.id !== c.id))} aria-label={`Remove calculation ${c.label}`}>
                <IconClose size={13} />
              </button>
            </div>
          )))}
          {calculations.length === 0 && (
            <div className="rf-shelf-hint">{form.measures.length ? 'Add a share of the total, a running total, a change from the previous row, or your own formula.' : 'Add a value first, then work things out from it here.'}</div>
          )}
        </div>
      </section>

      <Shelf id="groups" title="Group by" icon={<FieldIcon field={{ kind: 'dimension' }} size={14} />} hint="Add dimensions to split the values into rows or categories."
        count={form.dimensions.length} dragRef={dragRef} statusOf={statusOf}
        onDropField={(p, i) => actions.drop(p, 'groups', i)}
        onAdd={() => setPicker('groups')} addLabel="Add a group"
        picker={pickerFor('groups', (f) => actions.add(f, 'groups'), 'Add a group')}>
        {form.dimensions.map((d, i) => (
          <Chip key={d} name={d} field={fieldOf(d)} shelf="groups" index={i} dragRef={dragRef}
            sortState={orderOf(d)} onSort={() => cycleSort(d)} onRemove={() => actions.remove(d)} />
        ))}
        {form.dimensions.length > 0 && form.measures.length > 0 && <TopValues form={form} labelOf={labelOf} update={actions.update} />}
      </Shelf>

      <Shelf id="time" title="Time" icon={<IconCalendar size={14} />} hint="Add a date field to break values down over time or limit them to a period."
        count={form.timeDimension ? 1 : 0} dragRef={dragRef} statusOf={statusOf}
        onDropField={(p) => actions.drop(p, 'time', 0)}
        onAdd={form.timeDimension ? null : () => setPicker('time')} addLabel="Add a date field"
        picker={pickerFor('time', (f) => actions.add(f, 'time'), 'Add a date field')}>
        {form.timeDimension && (
          <>
            <Chip name={form.timeDimension} field={fieldOf(form.timeDimension)} shelf="time" index={0} dragRef={dragRef}
              sortState={timeBucket ? orderOf(timeBucket) : null} onSort={timeBucket ? () => cycleSort(timeBucket) : null}
              onRemove={() => actions.remove(form.timeDimension)} />
            <div className="rf-time-grid">
              <label>
                <span>Breakdown</span>
                <select aria-label="Breakdown" value={form.granularity} onChange={(e) => actions.update({ granularity: e.target.value })}>
                  {GRANULARITIES.map((g) => <option key={g.value} value={g.value}>{g.value ? g.label.replace(/^By (\w)/, (_, c) => c.toUpperCase()) : 'None'}</option>)}
                </select>
              </label>
              <div className="rf-time-field">
                <span>Period</span>
                <PeriodSelect ariaLabel="Period" value={form.dateRange} emptyLabel="All time" onChange={(v) => actions.update({ dateRange: v })} />
              </div>
            </div>
            <label className="rf-time-compare">
              <span>Compare with</span>
              <select aria-label="Compare with" value={form.compare || ''} onChange={(e) => actions.update({ compare: e.target.value })}>
                {COMPARE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <label className="rf-switch">
              <input type="checkbox" checked={form.inheritDateRange !== false} onChange={(e) => actions.update({ inheritDateRange: e.target.checked })} />
              <span>Follow the dashboard date range</span>
            </label>
          </>
        )}
      </Shelf>

      <Shelf id="filters" title="Filters" icon={<IconFilter size={14} />} hint="Add a field to limit which rows are included."
        count={form.filters.length} dragRef={dragRef} statusOf={statusOf}
        onDropField={(p) => actions.addFilter(p.field)}
        onAdd={() => setPicker('filters')} addLabel="Add a filter"
        picker={pickerFor('filters', (f) => actions.addFilter(f), 'Add a filter')}>
        {form.filters.map((f, i) => {
          const field = fieldOf(f.member);
          if (openFilter === i) {
            return (
              <FilterEditor key={i} filter={f} field={field} loadValues={loadValues}
                onChange={(next) => actions.updateFilter(i, next)}
                onRemove={() => actions.removeFilter(i)}
                onDone={() => setOpenFilter(null)} />
            );
          }
          return (
            <div key={i} className="rf-filter-chip">
              <button type="button" className="rf-filter-chip-main" onClick={() => setOpenFilter(i)} aria-label={`Edit filter: ${describeFilter(f, field)}`}>
                {field && <FieldIcon field={field} size={13} />}
                <span>{describeFilter(f, field)}</span>
              </button>
              <button type="button" className="rf-chip-btn" onClick={() => actions.removeFilter(i)} aria-label={`Remove filter on ${field?.label || f.member}`}>
                <IconClose size={13} />
              </button>
            </div>
          );
        })}
      </Shelf>

      <section className="rf-shelf rf-shelf--plain" aria-label="Sort and limit">
        <div className="rf-shelf-head">
          <span className="rf-shelf-icon"><IconSort size={14} /></span>
          <h4>Sort and limit</h4>
        </div>
        <div className="rf-sort-grid">
          <label>
            <span>Sort by</span>
            <select aria-label="Sort by" value={form.orderMember} onChange={(e) => actions.update({ orderMember: e.target.value })}>
              <option value="">Default</option>
              {orderable.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <div className="rf-segmented" role="radiogroup" aria-label="Sort direction">
            {[['desc', 'Descending'], ['asc', 'Ascending']].map(([v, l]) => (
              <button key={v} type="button" role="radio" aria-checked={form.orderDir === v} disabled={!form.orderMember}
                className={form.orderDir === v ? 'is-on' : ''} onClick={() => actions.update({ orderDir: v })}>{l}</button>
            ))}
          </div>
          <label>
            <span>Row limit</span>
            <input type="number" aria-label="Row limit" min="1" max="10000" value={form.limit} onChange={(e) => actions.update({ limit: e.target.value })} />
          </label>
        </div>
      </section>
    </div>
  );
};

export default BuildPanel;
